import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  lang?: "ar" | "en";
}

const FALLBACK_DOMAIN = "https://alfunoongroup.com";
// Self-referencing canonical: uses the domain actually serving the page,
// so it stays correct once the custom domain is connected.
const DOMAIN =
  typeof window !== "undefined" && window.location.origin
    ? window.location.origin
    : FALLBACK_DOMAIN;
const DEFAULT_OG_IMAGE = `${DOMAIN}/og-image.png`;

export function SEO({
  title,
  description,
  canonicalPath = "/",
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  jsonLd,
  lang = "ar",
}: SEOProps) {
  const canonicalUrl = `${DOMAIN}${canonicalPath}`;
  const fullTitle = title.includes("ALFUNOON") ? title : `${title} | ALFUNOON Group`;

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={lang === "ar" ? "ar_SA" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
